import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/input';
import {MatInput} from '@angular/material/input';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {TranslatePipe} from '@ngx-translate/core';
import {CertificateQueryService} from '../../../services/certificate-query.service';
import {ICertificateQueryResult} from '../../../shared/model/certificate-query-result.model';
import {MilestoneStatus} from '../../../shared/model/certificate-overview.model';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {CiMultiselectComponent} from '../../../../profiler-theme';

interface QuickRange {
  key: string;
  labelKey: string;
}

interface StatusConfig {
  icon: string;
  labelKey: string;
}

const CERT_DEFINITIONS: {display: string; fullName: string; backendKey: string}[] = [
  {display: 'CTFL',             fullName: 'ISTQB Certified Tester Foundation Level (CTFL)',                                  backendKey: 'ctfl'},
  {display: 'CT-AI',            fullName: 'ISTQB Certified Tester AI Testing (CT-AI)',                                       backendKey: 'ctAi'},
  {display: 'CT-GenAI',         fullName: 'ISTQB Certified Tester – Testing with Generative AI (CT-GenAI)',                  backendKey: 'ctGenAi'},
  {display: 'CTAL-TM',          fullName: 'ISTQB Certified Tester Advanced Level Test Management (CTAL-TM)',                 backendKey: 'ctalTm'},
  {display: 'CTAL-TA',          fullName: 'ISTQB Certified Tester Advanced Level Test Analysis (CTAL-TA)',                   backendKey: 'ctalTa'},
  {display: 'CTAL-TAE',         fullName: 'ISTQB Certified Tester Advanced Level Test Automation Engineering (CTAL-TAE)',    backendKey: 'ctalTae'},
  {display: 'CPRE-FL',          fullName: 'IREB Certified Professional for Requirements Engineering – Foundation Level (CPRE-FL)', backendKey: 'cprefl'},
  {display: 'CTFL-AT',          fullName: 'ISTQB Certified Tester Foundation Level Agile Tester (CTFL-AT)',                  backendKey: 'ctflAt'},
  {display: 'CT-FT',            fullName: 'ISTQB Certified Tester Finance Testing (CT-FT)',                                  backendKey: 'ctFt'},
  {display: 'A4Q AI Essentials',fullName: 'A4Q AI Essentials',                                                              backendKey: 'a4qAiEssentials'},
  {display: 'CT-AcT',           fullName: 'ISTQB Certified Tester Acceptance Testing (CT-AcT)',                              backendKey: 'ctAct'},
  {display: 'CT-PT',            fullName: 'ISTQB Certified Tester – Performance Testing (CT-PT)',                            backendKey: 'ctPt'},
  {display: 'CT-MAT',           fullName: 'ISTQB Certified Tester Mobile Application Testing (CT-MAT)',                      backendKey: 'ctMat'},
  {display: 'CT-MBT',           fullName: 'ISTQB Certified Tester Model-Based Testing (CT-MBT)',                             backendKey: 'ctMbt'},
  {display: 'CT-SEC',           fullName: 'ISTQB Certified Tester – Security Testing (CT-SEC)',                              backendKey: 'ctSec'},
  {display: 'CTAL',             fullName: 'ISTQB Certified Tester Advanced Level (CTAL)',                                    backendKey: 'ctal'},
  {display: 'CTAL-TTA',         fullName: 'ISTQB Certified Tester Advanced Level Technical Test Analyst (CTAL-TTA)',         backendKey: 'ctalTta'},
  {display: 'CTFL-AuT',         fullName: 'ISTQB Certified Tester Foundation Level Automotive Software Tester (CTFL-AuT)',   backendKey: 'ctflAut'},
];

const ALL_CERTS_LIST: string[] = CERT_DEFINITIONS.map(c => c.display);
const CERT_FULL_NAMES: Record<string, string> = Object.fromEntries(CERT_DEFINITIONS.map(c => [c.display, c.fullName]));
const CERT_KEY_MAP: Record<string, string> = Object.fromEntries(CERT_DEFINITIONS.map(c => [c.display, c.backendKey]));

const STATUS_ORDER: MilestoneStatus[] = ['training_planned', 'training_done', 'exam_planned', 'certified'];

const STATUS_CONFIG: Record<MilestoneStatus, StatusConfig> = {
  training_planned: {icon: 'schedule', labelKey: 'certificateOverview.milestoneStatus.training_planned'},
  training_done: {icon: 'school', labelKey: 'certificateOverview.milestoneStatus.training_done'},
  exam_planned: {icon: 'event', labelKey: 'certificateOverview.milestoneStatus.exam_planned'},
  certified: {icon: 'check_circle', labelKey: 'certificateOverview.milestoneStatus.certified'},
  none: {icon: '', labelKey: 'certificateOverview.milestoneStatus.none'},
};

@Component({
  selector: 'app-certificate-query',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DatePipe,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatIcon,
    MatTooltip,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    TranslatePipe,
    CiMultiselectComponent,
  ],
  templateUrl: './certificate-query.component.html',
  styleUrl: './certificate-query.component.scss'
})
export class CertificateQueryComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });

  dataSource = new MatTableDataSource<ICertificateQueryResult>();
  filterTerm = '';
  searched = false;
  loading = false;

  appliedFrom: Date | null = null;
  appliedTo: Date | null = null;
  appliedCerts: string[] = [];

  displayedColumns = ['certificationDate', 'consultantName', 'position', 'certificationName', 'hireDate', 'status'];

  activeChip: string | null = null;
  selectedCerts: string[] = [];
  statusCounts: {status: MilestoneStatus; count: number}[] = [];
  selectedStatuses = new Set<MilestoneStatus>();

  private certsAtLastSearch: string[] = [];

  readonly ALL_CERTS = ALL_CERTS_LIST;
  readonly STATUS_CONFIG = STATUS_CONFIG;
  readonly STATUS_FILTERS: MilestoneStatus[] = STATUS_ORDER;

  readonly QUICK_RANGES: QuickRange[] = [
    {key: 'lastMonth', labelKey: 'certificateQuery.lastMonth'},
    {key: 'last3Months', labelKey: 'certificateQuery.last3Months'},
    {key: 'last6Months', labelKey: 'certificateQuery.last6Months'},
    {key: 'thisYear', labelKey: 'certificateQuery.thisYear'},
    {key: 'custom', labelKey: 'certificateQuery.custom'},
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private certificateQueryService: CertificateQueryService,
  ) {
    this.dataSource.filterPredicate = (row) => {
      const matchesName = !this.filterTerm.trim()
        || row.consultantName.toLowerCase().includes(this.filterTerm.trim().toLowerCase());
      const matchesStatus = this.selectedStatuses.size === 0
        || (row.status && this.selectedStatuses.has(row.status));
      return matchesName && matchesStatus;
    };
    // NP20-880: Die Spalte heisst hireDate, das Feld consultantSince — ohne Zuordnung sortiert Material ins Leere.
    this.dataSource.sortingDataAccessor = (row, id) =>
      id === 'hireDate' ? (row.consultantSince ?? '') : ((row as unknown as Record<string, string>)[id] ?? '');
  }

  toggleStatusFilter(status: MilestoneStatus): void {
    if (this.selectedStatuses.has(status)) {
      this.selectedStatuses.delete(status);
    } else {
      this.selectedStatuses.add(status);
    }
    this.applyFilter();
  }

  isStatusSelected(status: MilestoneStatus): boolean {
    return this.selectedStatuses.has(status);
  }

  getStatusCount(status: MilestoneStatus): number {
    return this.statusCounts.find(s => s.status === status)?.count ?? 0;
  }

  private computeStatusCounts(): void {
    const counts = new Map<MilestoneStatus, number>();
    this.dataSource.data.forEach(row => {
      if (row.status && row.status !== 'none') {
        counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
      }
    });
    this.statusCounts = STATUS_ORDER
      .filter(s => counts.has(s))
      .map(s => ({status: s, count: counts.get(s)!}));
  }

  getCertFullName(name: string): string {
    return CERT_FULL_NAMES[name] ?? name;
  }

  ngOnInit(): void {
    this.dateRange.valueChanges.pipe(
      debounceTime(1200),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.dateRange.value.start) {
        this.activeChip = 'custom';
        this.search();
      }
    });
  }

  get hasCertFilter(): boolean {
    return this.selectedCerts.length > 0 && this.selectedCerts.length < this.ALL_CERTS.length;
  }

  resetCerts(): void {
    this.selectedCerts = [];
    this.certsAtLastSearch = [];
    this.appliedCerts = [];
    if (this.dateRange.value.start) {
      this.search();
    }
  }

  /**
   * Verglichen wird mit der letzten Suche, nicht mit dem Stand beim Oeffnen: das Panel
   * kann sich auch schliessen, ohne dass es sich meldet.
   */
  onCertSelectOpen(open: boolean): void {
    if (open) return;
    const certsChanged = this.certsAtLastSearch.length !== this.selectedCerts.length
      || this.selectedCerts.some(c => !this.certsAtLastSearch.includes(c));
    if (certsChanged && this.activeChip !== null && this.activeChip !== 'custom' && this.dateRange.value.start) {
      this.search();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectQuickRange(key: string): void {
    if (key === 'custom') {
      this.activeChip = 'custom';
      this.dateRange.patchValue({start: null, end: null}, {emitEvent: false});
      return;
    }

    const today = new Date();
    let start: Date;
    const end = new Date(today);

    switch (key) {
      case 'lastMonth':
        start = new Date(today);
        start.setMonth(start.getMonth() - 1);
        break;
      case 'last3Months':
        start = new Date(today);
        start.setMonth(start.getMonth() - 3);
        break;
      case 'last6Months':
        start = new Date(today);
        start.setMonth(start.getMonth() - 6);
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    this.activeChip = key;
    this.dateRange.patchValue({start, end}, {emitEvent: false});
    this.search();
  }

  applyFilter(): void {
    this.dataSource.filter = `${this.filterTerm}|${Array.from(this.selectedStatuses).join(',')}`;
  }

  search(): void {
    const start = this.dateRange.value.start;
    if (!start) return;
    const end = this.dateRange.value.end ?? new Date();

    this.loading = true;
    this.searched = false;
    this.dataSource.data = [];
    this.selectedStatuses.clear();

    const from = this.toIsoDate(start);
    const to = this.toIsoDate(end);
    const certNames = this.selectedCerts.length > 0
      ? this.selectedCerts.map(name => CERT_KEY_MAP[name]).filter(Boolean)
      : undefined;
    // Hier, nicht erst in der Antwort: sonst zaehlt ein Schliessen waehrend der laufenden
    // Anfrage denselben Filter erneut als Aenderung.
    this.certsAtLastSearch = [...this.selectedCerts];

    this.certificateQueryService.query(from, to, certNames)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: results => {
          this.dataSource.data = results;
          this.filterTerm = '';
          this.dataSource.filter = '';
          this.appliedFrom = new Date(start);
          this.appliedTo = new Date(end);
          this.appliedCerts = [...this.selectedCerts];
          this.computeStatusCounts();
          this.searched = true;
          this.loading = false;
        },
        error: () => {
          this.dataSource.data = [];
          this.statusCounts = [];
          this.searched = true;
          this.loading = false;
        }
      });
  }

  private toIsoDate(date: Date): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }
}
